import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { TableCell, TableRow } from '@/components/ui/table'
import { Call, determineCallResult, hasSmsAction } from '@/types/calls'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import {
  Check,
  HelpCircle,
  Mail,
  Phone,
  RefreshCw,
  Triangle,
  Users,
  X
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface CallRowProps {
  call: Call
  selected: boolean
  onSelect: () => void
  onReview: (reviewed: boolean) => void
  onDetails: () => void
}

export function CallRow({ call, selected, onSelect, onReview, onDetails }: CallRowProps) {
  const { t } = useTranslation()
  // Handle checkbox change - mark as reviewed when checked
  const handleCheckboxChange = () => {
    onSelect();
    onReview(!selected); // Toggle reviewed status
  };
  // Format call time
  const formatCallTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime)
    const end = new Date(endTime)
    const dateStr = format(start, 'dd.MM.yyyy', { locale: de })
    const timeRange = `${format(start, 'HH:mm', { locale: de })} - ${format(end, 'HH:mm', { locale: de })}`
    return { dateStr, timeRange }
  }

  // Get result icons
  const getResultIcons = () => {
    const result = determineCallResult(call)
    const hasSms = hasSmsAction(call.actions)
    const icons = []

    // Check for forwarded calls via conversationForwardState
    const forwardAction = call.actions.find(a => a.data?.conversationForwardState)
    const forwardState = forwardAction?.data?.conversationForwardState
    const isForwardSuccessful = forwardState === 'FORWARD_SUCCESS'

    // Primary result icon - Priorität: SUCCESS > FAILURE > FORWARD > OTHER
    switch (result) {
      case 'SUCCESS':
        // Erfolgreiche Reservierung - immer grüner Haken, auch wenn Forward versucht wurde
        icons.push(
          <div key="success" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50">
            <Check className="h-5 w-5 text-green-600 stroke-[2.5]" />
          </div>
        )
        break
      case 'FAILURE':
        icons.push(
          <div key="failure" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50">
            <X className="h-5 w-5 text-red-600 stroke-[2.5]" />
          </div>
        )
        break
      case 'FORWARDED':
        // Nur Forward als Hauptergebnis
        if (isForwardSuccessful) {
          icons.push(
            <div key="forwarded" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-50">
              <Phone className="h-5 w-5 text-green-600 stroke-[2]" />
            </div>
          )
        } else {
          icons.push(
            <div key="forwarded-failed" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-50">
              <Phone className="h-5 w-5 text-red-600 stroke-[2]" />
            </div>
          )
        }
        break
      case 'OTHER':
        icons.push(
          <div key="other" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-50">
            <Phone className="h-5 w-5 text-gray-600 stroke-[2]" />
          </div>
        )
        break
      case 'CANCELLATION':
        icons.push(
          <div key="cancellation" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-50">
            <RefreshCw className="h-5 w-5 text-orange-600 stroke-[2]" />
          </div>
        )
        break
      default:
        icons.push(
          <div key="unknown" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
            <HelpCircle className="h-5 w-5 text-gray-500 stroke-[2]" />
          </div>
        )
    }

    // Additional icons for call actions
    if (hasSms) {
      icons.push(
        <div key="sms" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-50">
          <Mail className="h-5 w-5 text-purple-600 stroke-[2]" />
        </div>
      )
    }

    return icons
  }

  // Format caller info
  const callerName = call.callerName || 'N. A.'
  const callerPhone = call.callerPhone || 'Unknown'

  // Format booking time - zweizeilig
  const bookingTime = call.bookingTime
    ? {
      date: format(new Date(call.bookingTime), 'dd.MM.yyyy', { locale: de }),
      time: format(new Date(call.bookingTime), 'HH:mm', { locale: de })
    }
    : null

  // Format booking ID with delta icon
  const bookingIdDisplay = call.bookingId ? (
    <div className="flex items-center gap-2">
      <Triangle className="h-3.5 w-3.5 text-purple-500 fill-purple-500" />
      <span className="text-sm font-mono text-gray-900">{call.bookingId.substring(0, 18)}...</span>
    </div>
  ) : (
    <span className="text-gray-400 text-base">–</span>
  )

  const { dateStr, timeRange } = formatCallTime(call.startTime, call.endTime)

  return (
    <TableRow className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 h-20">
      {/* Selection checkbox */}
      <TableCell className="pl-6 w-12 py-4">
        <Checkbox
          checked={selected || call.reviewed}
          onCheckedChange={handleCheckboxChange}
          aria-label={`Mark call ${call.sessionId} as reviewed`}
        />
      </TableCell>

      {/* Call time */}
      <TableCell className="whitespace-nowrap">
        <div className="space-y-1">
          <div className="font-semibold text-gray-900 text-base">{dateStr}</div>
          <div className="text-sm text-gray-600">{timeRange}</div>
        </div>
      </TableCell>

      {/* Result icons */}
      <TableCell className="whitespace-nowrap">
        <div className="flex items-center gap-2">
          {getResultIcons()}
        </div>
      </TableCell>

      {/* Caller info */}
      <TableCell>
        <div className="space-y-1">
          <div className="font-semibold text-gray-900 text-base">{callerName}</div>
          <div className="text-sm text-gray-600">{callerPhone}</div>
        </div>
      </TableCell>

      {/* Booking time */}
      <TableCell className="whitespace-nowrap">
        {bookingTime ? (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{bookingTime.date}</div>
            <div className="text-sm text-gray-600">{bookingTime.time}</div>
          </div>
        ) : (
          <span className="text-gray-400 text-base">–</span>
        )}
      </TableCell>

      {/* Guests */}
      <TableCell className="whitespace-nowrap">
        {call.guestCount ? (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-500" />
            <span className="font-semibold text-gray-900 text-base">{call.guestCount}</span>
          </div>
        ) : (
          <span className="text-gray-400 text-base">–</span>
        )}
      </TableCell>

      {/* Booking ID */}
      <TableCell className="min-w-[200px]">
        {bookingIdDisplay}
      </TableCell>

      {/* Details button */}
      <TableCell className="text-right pr-6 w-32">
        <Button
          size="sm"
          onClick={onDetails}
          className="bg-brand-primary hover:bg-brand-primary-dark text-white font-medium text-sm px-4 py-2 h-9 shadow-sm"
        >
          {t('callsTable.details')}
        </Button>
      </TableCell>
    </TableRow>
  )
}
